import { useState } from "react";
import type { FC } from "react";
import {
  Menu,
  MenuItem,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faEllipsis,
  faEye,
  faPen,
  faCopy,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface TableItemMenuProps {
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  disableView?: boolean;
  disableEdit?: boolean;
  disableDuplicate?: boolean
  disableDelete?: boolean;
  extraActions?: { label: string; onClick: () => void }[];
}

export const TableItemMenu: FC<TableItemMenuProps> = (props) => {
  const { t } = useTranslation();
  const { onView, onEdit, onDuplicate, onDelete, disableView, disableEdit, disableDuplicate, disableDelete, extraActions } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        color="primary"
        size="small"
        sx={{ ':hover': { backgroundColor: 'transparent' }}}
      >
        <FontAwesomeIcon icon={faEllipsis as IconProp} />
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        elevation={10}
        sx={{
          '.MuiMenu-paper': {
            border: 'none',
            backgroundColor: 'background.default'
          },
          '.MuiMenu-list': {
            color: 'primary.main',
            '.MuiMenuItem-root:hover': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText'
            }
          },
        }}
      >
        {extraActions && extraActions.map((a, i) => (
          <MenuItem key={i} onClick={() => { a.onClick(); handleClose(); }}>
            {a.label}
          </MenuItem>
        ))}
        {!disableView && (
          <MenuItem onClick={() => { onView(); handleClose(); }}>
            <FontAwesomeIcon icon={faEye as IconProp} style={{ marginRight: 10 }} />
            {t('view')}
          </MenuItem>
        )}
        {!disableEdit && (
          <MenuItem onClick={() => { onEdit(); handleClose(); }}>
            <FontAwesomeIcon icon={faPen as IconProp} style={{ marginRight: 10 }} />
            {t('edit')}
          </MenuItem>
        )}
        {!disableDuplicate && (
          <MenuItem onClick={() => { onDuplicate(); handleClose(); }}>
            <FontAwesomeIcon icon={faCopy as IconProp} style={{ marginRight: 10 }} />
            {t('duplicate')}
          </MenuItem>
        )}
        {!disableDelete && (
          <>
            <Divider />
            <MenuItem onClick={() => { setOpenDeleteConfirmation(true); handleClose(); }}>
              <FontAwesomeIcon icon={faTrash as IconProp} style={{ marginRight: 10 }} />
              {t('delete')}
            </MenuItem>
          </>
        )}
      </Menu>
      <Dialog open={openDeleteConfirmation} onClose={() => setOpenDeleteConfirmation(false)}>
        <DialogTitle>{t('delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('confirm_delete_item')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirmation(false)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { setOpenDeleteConfirmation(false); onDelete(); }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
